// "Send for review": the sample payout contract mailed to the people a vendor wants to look at it
// before theirs comes — an accountant, a lawyer (store {payouts_contract_share}). The addresses as
// chips, as the invite dialog takes them (aldoo-ra/Organizations/InviteUsersDialog), a note to them,
// and Send; then who it reached. The PDF goes attached; an answer comes back to the sender.
import React, { useState } from "react"
import Modal from "aldoo-ra/Modal"
import Button from "aldoo-ra/Button"
import useIsMobile from "aldoo-ra/Hooks/use-is-mobile"
import { XMarkIcon, CheckIcon } from "@heroicons/react/24/outline"
import { TextArea } from "../AdminShared/ui"
import { PayoutsContractShare } from "../AdminShared/api"

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const MAX = 10
const addressesIn = (text) =>
  String(text || "")
    .split(/[,;\s]+/)
    .map((part) => part.trim().toLowerCase())
    .filter((part) => EMAIL.test(part))

export default function ShareContractDialog({ orgId, t, onClose }) {
  const isMobile = useIsMobile()
  const [input, setInput] = useState("")
  const [emails, setEmails] = useState([])
  const [note, setNote] = useState("")
  const [sending, setSending] = useState(false)
  const [error, setError] = useState("")
  const [sent, setSent] = useState(null)

  const add = () => {
    const found = addressesIn(input)
    if (!found.length) return
    setEmails((prev) => [...new Set([...prev, ...found])])
    setInput("")
  }
  const onKeyDown = (e) => {
    if (e.key === "Enter" || e.key === "," || e.key === " ") {
      e.preventDefault()
      add()
    }
    if (e.key === "Backspace" && !input) setEmails((prev) => prev.slice(0, -1))
  }
  // what is typed and not made a chip yet goes too
  const batch = [...new Set([...emails, ...addressesIn(input)])]

  const send = async () => {
    if (!batch.length) return
    if (batch.length > MAX) return setError(t("At most %s addresses at a time", MAX))
    setSending(true)
    setError("")
    const res = await PayoutsContractShare(orgId, batch, note).catch(() => null)
    setSending(false)
    if (!res || res.error) {
      const known = { invalid_email: t`Check the email addresses`, too_many: t("At most %s addresses at a time", MAX), daily_limit: t`You have sent the contract to many people today. Try again tomorrow.` }
      return setError(known[res?.error] || t`The contract could not be sent. Try again.`)
    }
    setSent(res.sent || [])
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      variant={isMobile ? "admin_fullScreen" : "admin_centered"}
      className={isMobile ? "" : "max-w-lg"}
      title={t`Send the contract for review`}
      closeOnEscape
      // sent: what it reached in the middle of the space above the buttons (the variant's own padding kept)
      styleOverrides={sent ? { content: `${isMobile ? "px-4 pb-4 md:px-6 md:pb-6" : "px-5 py-5 sm:px-6 sm:py-6"} flex flex-col justify-center` } : undefined}
      // the buttons in the dialog's footer, at its bottom whatever its height
      footerContent={
        sent ? (
          <div className="flex gap-3">
            <Button variant="admin_secondary" className="flex-1" onClick={onClose}>
              {t`Done`}
            </Button>
            <Button variant="admin_primary" className="flex-1" onClick={() => (setSent(null), setEmails([]), setInput(""))}>
              {t`Send to others`}
            </Button>
          </div>
        ) : (
          <div className="flex justify-end">
            <Button variant="admin_primary" onClick={send} disabled={sending || !batch.length} loading={sending} data-testid="contract-share-send">
              {t`Send`}
            </Button>
          </div>
        )
      }
    >
      {sent ? (
        <div className="flex flex-col gap-4" data-testid="contract-share-done">
          <div className="flex flex-col items-center gap-3 py-2 text-center">
            <div className="w-16 h-16 rounded-full bg-admin_success/15 flex items-center justify-center">
              <CheckIcon className="w-8 h-8 text-admin_success" />
            </div>
            <p className="m-0 text-xl font-semibold text-admin_text">{sent.length === 1 ? t`The contract was sent` : t("The contract was sent to %s people", sent.length)}</p>
            <p className="m-0 text-base text-admin_text_secondary">{sent.join(", ")}</p>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-4" data-testid="contract-share">
          <p className="m-0 text-sm text-admin_text_secondary">
            {t`We will email the contract, with sample data, to the people you enter here: your accountant, a lawyer, a partner. Their answers come back to you.`}
          </p>
          <div>
            <p className="m-0 mb-1.5 text-sm font-semibold text-admin_text">{t`Email addresses`}</p>
            <div
              className="flex flex-wrap items-center gap-1.5 px-3 py-2 rounded-md border border-admin_bordered bg-admin_paper min-h-[74px] content-start cursor-text focus-within:ring-2 focus-within:ring-admin_primary focus-within:border-admin_primary"
              onClick={(e) => e.currentTarget.querySelector("input")?.focus()}
            >
              {emails.map((email) => (
                <span key={email} className="flex items-center gap-1 bg-admin_primary/15 text-admin_primary text-xs px-2.5 py-1 rounded-full">
                  {email}
                  <button type="button" onClick={() => setEmails((prev) => prev.filter((e) => e !== email))} className="hover:text-admin_primary_dk ml-0.5" aria-label={t`Remove`}>
                    <XMarkIcon className="w-3 h-3" />
                  </button>
                </span>
              ))}
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                onBlur={add}
                placeholder={emails.length ? "" : "accountant@example.com"}
                className="flex-1 min-w-[160px] bg-transparent text-sm text-admin_text placeholder-admin_text_secondary outline-none py-1"
                data-testid="contract-share-emails"
              />
            </div>
          </div>
          <div>
            <p className="m-0 mb-1.5 text-sm font-semibold text-admin_text">
              {t`Note`} <span className="font-normal text-admin_text_secondary">({t`optional`})</span>
            </p>
            <TextArea value={note} onChange={setNote} rows={3} maxLength={1000} placeholder={t`Could you take a look at this contract?`} data-testid="contract-share-note" />
          </div>
          {error && <p className="m-0 text-sm text-admin_error">{error}</p>}
        </div>
      )}
    </Modal>
  )
}
